import { useState, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import JWTContext from 'contexts/JWTContext';

/**
 * ImpersonateUserModal - Admin-only modal to impersonate a target user.
 */
export default function ImpersonateUserModal({ open, onClose }) {
    const { impersonate } = useContext(JWTContext);
    const [targetUserId, setTargetUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleImpersonate = async () => {
        if (!targetUserId.trim()) {
            setError('Please enter a user ID');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await impersonate(targetUserId.trim());
            setTargetUserId('');
            onClose();
        } catch (err) {
            const message = err.response?.data?.error || err.response?.data?.detail || err.message;
            setError(message || 'Failed to impersonate user');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setTargetUserId('');
            setError('');
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Impersonate User</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter the user ID of the account you want to impersonate. The target user must have an active support grant.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    autoFocus
                    fullWidth
                    label="Target User ID"
                    placeholder="usr_..."
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                    disabled={loading}
                    sx={{ fontFamily: 'monospace' }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handleImpersonate}
                    disabled={loading || !targetUserId.trim()}
                    startIcon={loading && <CircularProgress size={16} color="inherit" />}
                >
                    {loading ? 'Impersonating...' : 'Impersonate'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
